import { PlayIcon, PauseIcon } from '@heroicons/react/24/solid';
import { useTranslation } from 'react-i18next';
import { useAudioPlayer } from '@/data/hook/shared/useAudioPlayer';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';

type AudioPlayerProps = {
  src: string;
  name?: string;
};

export function AudioPlayer({ src, name }: AudioPlayerProps) {
  const [t] = useTranslation('translation', { keyPrefix: 'audioPlayer' });
  const { audioRef, isPlaying, currentTime, duration, togglePlay, seek } =
    useAudioPlayer();

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className="relative flex items-center gap-3 border border-primary rounded-lg px-4 py-3 w-full">
      <audio ref={audioRef} src={src} />

      <button
        type="button"
        onClick={togglePlay}
        aria-label={isPlaying ? t('pause') : t('play')}
        className="shrink-0 w-10 h-10 rounded-full bg-primary flex items-center justify-center text-primary-content"
      >
        {isPlaying ? (
          <PauseIcon className="w-5 h-5" />
        ) : (
          <PlayIcon className="w-5 h-5 ml-0.5" />
        )}
      </button>

      <div className="flex-1 min-w-0 flex items-center">
        <ConditionalRenderer
          condition={isPlaying}
          fallback={
            <span className="text-base-content font-medium truncate">
              {name || 'Áudio'}
            </span>
          }
        >
          <div className="relative h-5 w-full flex items-center group">
            <div className="h-1 w-full bg-base-300 rounded-full overflow-hidden">
              <div
                className="h-full bg-primary transition-[width] duration-100"
                style={{ width: `${progress}%` }}
              />
            </div>
            <div
              className="absolute w-3 h-3 bg-primary rounded-full -translate-x-1/2 shadow pointer-events-none"
              style={{ left: `${progress}%` }}
            />
            <input
              type="range"
              min={0}
              max={duration || 0}
              step={0.1}
              value={currentTime}
              onChange={e => seek(Number(e.target.value))}
              aria-label={t('progress')}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            />
          </div>
        </ConditionalRenderer>
      </div>
    </div>
  );
}
